import { httpsCallable } from "firebase/functions";
import getFirebase from "./firebase";

type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogException {
    name?: string;
    message: string;
    stack?: string;
}

/**
 * Context supplied at call sites. `exception` (or the legacy `error` alias)
 * maps to the canonical exception field; every other key is auto-collected
 * into the canonical `data` bucket, so call sites only pass what is needed to
 * diagnose the log (e.g. the failing object, ids, request data).
 */
export interface LogContext {
    exception?: unknown;
    /** Legacy alias for `exception`. */
    error?: unknown;
    [key: string]: unknown;
}

/**
 * Canonical log entry. Six universal fields, mirroring what is needed to
 * diagnose a log: where it was triggered (logger), what happened (message),
 * severity (level), error details (exception), who triggered it (identity -
 * stamped server-side, never trusted from the client) and anything else
 * required to diagnose it (data).
 */
export interface LogEntry {
    logger: string;
    message: string;
    level: LogLevel;
    exception: LogException | null;
    identity: string | null;
    data: Record<string, unknown>;
}

const FLUSH_INTERVAL_MS = 5000;
const FLUSH_BATCH_SIZE = 10;
const MAX_QUEUE_SIZE = 100;
const MAX_MESSAGE_CHARS = 2000;
const MAX_DATA_CHARS = 8192;

const APP_VERSION = process.env.REACT_APP_VERSION || "Portal";
const IS_DEV = process.env.NODE_ENV === "development";

/**
 * Client-side level filter. Defaults to `debug` in development builds and
 * `info` in production; override per build with REACT_APP_LOG_LEVEL.
 * Entries below this level are dropped entirely (not queued, not shipped).
 */
const LOG_LEVEL: LogLevel =
    (process.env.REACT_APP_LOG_LEVEL as LogLevel) || (IS_DEV ? "debug" : "info");

const LEVEL_RANK: Record<LogLevel, number> = { debug: 1, info: 2, warn: 3, error: 4 };

const RESERVED_KEYS = new Set([
    "logger",
    "message",
    "level",
    "exception",
    "error",
    "identity",
    "userId",
    "uid",
    "data",
    "page",
    "version",
    "traceId",
]);

const ANON_ID_KEY = "symmio.log.anonId";

let queue: LogEntry[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushing = false;

const getPage = (): string => {
    if (typeof window === "undefined") return "unknown";
    return `${window.location.pathname}${window.location.search}` || "unknown";
};

const generateSessionId = (prefix: string): string => {
    try {
        const arr = new Uint32Array(2);
        crypto.getRandomValues(arr);
        return `${prefix}-${arr[0].toString(36)}-${arr[1].toString(36)}-${Date.now().toString(36)}`;
    } catch {
        return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
};

const traceId = generateSessionId("sess");

const getAnonId = (): string | null => {
    if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
        return null;
    }
    try {
        let anonId = window.localStorage.getItem(ANON_ID_KEY);
        if (!anonId) {
            anonId = generateSessionId("anon");
            window.localStorage.setItem(ANON_ID_KEY, anonId);
        }
        return anonId;
    } catch {
        return null;
    }
};

const normalizeException = (value: unknown): LogException | null => {
    if (value == null) {
        return null;
    }
    if (value instanceof Error) {
        return { name: value.name, message: value.message, stack: value.stack };
    }
    if (typeof value === "string") {
        return { message: value };
    }
    if (typeof value === "object" && !Array.isArray(value)) {
        const ex = value as Record<string, unknown>;
        return {
            name: typeof ex.name === "string" ? ex.name : undefined,
            message: typeof ex.message === "string" ? ex.message : String(value),
            stack: typeof ex.stack === "string" ? ex.stack : undefined,
        };
    }
    return { message: String(value) };
};

const sanitizeValue = (
    value: unknown,
    depth: number,
    seen: Set<unknown>
): unknown => {
    if (value === null || value === undefined) {
        return undefined;
    }

    const type = typeof value;
    if (type === "string" || type === "boolean") {
        return value;
    }
    if (type === "number") {
        return Number.isFinite(value as number) ? value : String(value);
    }
    if (type === "bigint") {
        return value.toString();
    }
    if (type === "function" || type === "symbol") {
        return undefined;
    }

    if (value instanceof Error) {
        return { name: value.name, message: value.message, stack: value.stack };
    }
    if (value instanceof Date) {
        return value.toISOString();
    }
    if (typeof value === "object") {
        if (seen.has(value)) {
            return "[circular]";
        }
        if (depth > 6) {
            return "[maxDepth]";
        }
        seen.add(value);

        // Firestore Timestamp and other date-like objects expose toDate()
        const obj = value as { toDate?: () => unknown };
        if (typeof obj.toDate === "function") {
            const date = obj.toDate();
            if (date instanceof Date && !isNaN(date.getTime())) {
                return date.toISOString();
            }
        }

        if (Array.isArray(value)) {
            const items = value
                .map((item) => sanitizeValue(item, depth + 1, seen))
                .filter((item) => item !== undefined);
            return items.length > 50
                ? items.slice(0, 50).concat([`[...truncated ${items.length - 50} items]`])
                : items;
        }

        const result: Record<string, unknown> = {};
        for (const [key, item] of Object.entries(value)) {
            const sanitized = sanitizeValue(item, depth + 1, seen);
            if (sanitized !== undefined) {
                result[key] = sanitized;
            }
        }
        return result;
    }

    return String(value);
};

/** @internal Exported for unit tests. */
export const sanitizeData = (data: Record<string, unknown>): Record<string, unknown> => {
    const sanitized = sanitizeValue(data, 0, new Set()) as Record<string, unknown>;

    let json: string;
    try {
        json = JSON.stringify(sanitized);
    } catch {
        return { truncated: true, preview: "<unserializable data>" };
    }

    if (json.length > MAX_DATA_CHARS) {
        let slice = json.slice(0, MAX_DATA_CHARS);
        const lastCharCode = slice.charCodeAt(slice.length - 1);
        if (lastCharCode >= 0xd800 && lastCharCode <= 0xdbff) {
            slice = slice.slice(0, -1);
        }
        return { truncated: true, preview: `${slice}...` };
    }

    return sanitized;
};

/** @internal Exported for unit tests. */
export const normalizeEntry = (
    loggerName: string,
    level: LogLevel,
    message: string,
    context?: LogContext
): LogEntry => {
    const extras: Record<string, unknown> = {};
    if (context != null) {
        if (typeof context === "object" && !Array.isArray(context)) {
            for (const [key, value] of Object.entries(context)) {
                if (!RESERVED_KEYS.has(key)) {
                    extras[key] = value;
                }
            }
        } else {
            extras.detail = context;
        }
    }

    const anonId = getAnonId();
    const data: Record<string, unknown> = {
        page: getPage(),
        version: APP_VERSION,
        source: "portal",
        traceId,
        ...extras,
    };
    if (anonId != null) {
        data.anonId = anonId;
    }

    const trimmedMessage = message.length > MAX_MESSAGE_CHARS
        ? `${message.slice(0, MAX_MESSAGE_CHARS)}...`
        : message;

    return {
        logger: loggerName,
        message: trimmedMessage,
        level,
        exception: normalizeException(context?.exception ?? context?.error),
        identity: null,
        data: sanitizeData(data),
    };
};

const scheduleFlush = () => {
    if (flushTimer !== null) {
        return;
    }

    flushTimer = setTimeout(() => {
        flushTimer = null;
        void flush();
    }, FLUSH_INTERVAL_MS);
};

const log = (level: LogLevel, loggerName: string, message: string, context?: LogContext) => {
    if (LEVEL_RANK[level] < LEVEL_RANK[LOG_LEVEL]) {
        return;
    }

    const entry = normalizeEntry(loggerName, level, message, context);

    if (IS_DEV) {
        const fn =
            level === "error"
                ? console.error
                : level === "warn"
                    ? console.warn
                    : level === "debug"
                        ? console.debug
                        : console.info;
        fn(`[${level.toUpperCase()}] [${loggerName}] ${message}`, context ?? "");
    }

    if (queue.length >= MAX_QUEUE_SIZE) {
        queue.shift();
    }

    queue.push(entry);

    if (queue.length >= FLUSH_BATCH_SIZE) {
        void flush();
    } else {
        scheduleFlush();
    }
};

const sendBatch = async (batch: LogEntry[]) => {
    const { functions } = getFirebase();
    const callable = httpsCallable<{ entries: LogEntry[] }, { success: boolean }>(
        functions,
        "logEvent"
    );
    await callable({ entries: batch });
};

const flush = async () => {
    if (flushing || queue.length === 0) {
        return;
    }

    flushing = true;
    const batch = queue.splice(0, FLUSH_BATCH_SIZE);

    try {
        await sendBatch(batch);
    } catch (err) {
        if (IS_DEV) {
            console.warn("[Logger] Failed to send log batch", err);
        }
        try {
            await sendBatch(batch);
        } catch (retryErr) {
            if (IS_DEV) {
                console.warn("[Logger] Failed to send log batch after retry", retryErr);
            }
        }
    } finally {
        flushing = false;
        if (queue.length > 0) {
            scheduleFlush();
        }
    }
};

export interface Logger {
    debug: (message: string, context?: LogContext) => void;
    info: (message: string, context?: LogContext) => void;
    warn: (message: string, context?: LogContext) => void;
    error: (message: string, context?: LogContext) => void;
}

/**
 * Creates a named logger. The name is recorded in the canonical `logger` field
 * so every entry identifies where it was triggered. Extra context keys passed
 * to the log methods are auto-collected into the canonical `data` bucket.
 */
export function createLogger(name: string): Logger {
    return {
        debug: (message, context) => log("debug", name, message, context),
        info: (message, context) => log("info", name, message, context),
        warn: (message, context) => log("warn", name, message, context),
        error: (message, context) => log("error", name, message, context),
    };
}